import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Accordion from "@/components/base/Accordion";
import { MAIN_SITE_URL } from "@/mocks/site";
import { pillarArticles } from "@/mocks/articles";

// Gallery images — hardcoded literal URLs for editorial product shots
const galleryImage1 =
  "https://readdy.ai/api/search-image?query=Flat%20lay%20of%20premium%20sneakers%20and%20streetwear%20accessories%20arranged%20on%20a%20marble%20surface%20with%20warm%20golden%20hour%20lighting%2C%20luxury%20editorial%20product%20photography%2C%20soft%20shadows%2C%20clean%20minimal%20composition%2C%20high%20fashion%20aesthetic&width=900&height=600&seq=gallery-editorial-1&orientation=landscape";
const galleryImage2 =
  "https://readdy.ai/api/search-image?query=Close%20up%20of%20a%20designer%20leather%20bag%20and%20matching%20accessories%20on%20a%20textured%20linen%20backdrop%2C%20warm%20neutral%20tones%2C%20editorial%20fashion%20photography%2C%20soft%20natural%20light%2C%20elegant%20minimalist%20styling&width=900&height=600&seq=gallery-editorial-2&orientation=landscape";
const galleryImage3 =
  "https://readdy.ai/api/search-image?query=Overhead%20view%20of%20neatly%20folded%20premium%20hoodies%20and%20t-shirts%20in%20earth%20tones%20on%20a%20concrete%20surface%20with%20soft%20daylight%2C%20streetwear%20editorial%20photography%2C%20clean%20aesthetic%2C%20warm%20shadows&width=900&height=600&seq=gallery-editorial-3&orientation=landscape";

// Inline article images
const inlineImage1 =
  "https://readdy.ai/api/search-image?query=Person%20hands%20typing%20on%20a%20laptop%20displaying%20a%20spreadsheet%20on%20a%20bright%20modern%20desk%20with%20coffee%20cup%20and%20notebook%2C%20warm%20natural%20window%20light%2C%20editorial%20workspace%20photography%2C%20shallow%20depth%20of%20field%2C%20clean%20minimal%20background&width=1100&height=550&seq=inline-body-1&orientation=landscape";
const inlineImage2 =
  "https://readdy.ai/api/search-image?query=Quality%20control%20inspection%20of%20a%20folded%20garment%20with%20a%20magnifying%20glass%20and%20checklist%20on%20a%20white%20table%2C%20bright%20studio%20lighting%2C%20product%20photography%20detail%20shot%2C%20clean%20professional%20aesthetic&width=1100&height=550&seq=inline-body-2&orientation=landscape";
const inlineImage3 =
  "https://readdy.ai/api/search-image?query=Cardboard%20shipping%20box%20with%20packing%20tape%20and%20shipping%20labels%20on%20a%20light%20wooden%20table%2C%20logistics%20e-commerce%20flat%20lay%20photography%2C%20warm%20soft%20lighting%2C%20minimal%20clean%20composition&width=1100&height=550&seq=inline-body-3&orientation=landscape";

const inlineImages = [inlineImage1, inlineImage2, inlineImage3];

export default function ArticlePage() {
  const { t } = useTranslation();
  const { slug } = useParams();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const article = pillarArticles.find((a) => a.slug === slug);

  if (!article) {
    return <Navigate to="/" replace />;
  }

  const prefix = article.keyPrefix;

  const tocItems = article.sections.map((s) => s.h2Key);

  const faqItems = Array.from({ length: article.faqCount }, (_, i) => ({
    q: t(`${prefix}.faq.q${i + 1}`),
    a: t(`${prefix}.faq.a${i + 1}`),
  }));

  const related = pillarArticles
    .filter((a) => a.slug !== slug)
    .slice(0, 3);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* ========== HERO ========== */}
      <section className="relative h-[70vh] min-h-[420px] w-full overflow-hidden">
        <img
          src={article.coverImage}
          alt={t(`${prefix}.title`)}
          className="h-full w-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/70" />

        <div className="absolute inset-0 flex items-end justify-center pb-16 md:pb-24">
          <div className="mx-auto w-full max-w-4xl px-4 text-center md:px-6">
            <span className="inline-flex rounded-full border border-white/30 bg-white/10 px-4 py-1.5 text-xs font-medium uppercase tracking-wider text-white backdrop-blur-sm">
              {t(`${prefix}.category`)}
            </span>
            <h1 className="mt-5 text-3xl font-bold leading-tight text-white md:text-5xl lg:text-6xl">
              {t(`${prefix}.title`)}
            </h1>
            <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-white/80 md:text-base">
              {t(`${prefix}.lead`)}
            </p>
          </div>
        </div>
      </section>

      {/* ========== META BAR ========== */}
      <div className="border-b border-background-200 bg-white">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-center gap-x-6 gap-y-2 px-4 py-4 text-xs text-foreground-500 md:px-6">
          <span className="flex items-center gap-1.5">
            <i className="ri-user-line text-sm w-4 h-4 flex items-center justify-center"></i>
            {t("article.author")}
          </span>
          <span className="flex items-center gap-1.5">
            <i className="ri-shield-check-line text-sm w-4 h-4 flex items-center justify-center"></i>
            {t("article.reviewer")}
          </span>
          <span className="flex items-center gap-1.5">
            <i className="ri-calendar-line text-sm w-4 h-4 flex items-center justify-center"></i>
            {t("article.updated")}
          </span>
          <span className="flex items-center gap-1.5">
            <i className="ri-time-line text-sm w-4 h-4 flex items-center justify-center"></i>
            {t(`${prefix}.readTime`)}
          </span>
        </div>
      </div>

      <main>
        {/* ========== TABLE OF CONTENTS GRID ========== */}
        <section className="mx-auto max-w-6xl px-4 py-14 md:px-6 md:py-20">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-foreground-400">
              {t("article.toc")}
            </h2>
          </div>
          <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {tocItems.map((key, idx) => (
              <a
                key={key}
                href={`#section-${idx + 1}`}
                className="group flex items-start gap-4 rounded-lg border border-background-200 bg-white p-5 transition-all hover:border-primary-300 hover:bg-primary-50/40 cursor-pointer"
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-100 text-sm font-bold text-primary-700">
                  {String(idx + 1).padStart(2, "0")}
                </span>
                <span className="pt-1 text-sm font-semibold leading-snug text-foreground-800 transition-colors group-hover:text-primary-700">
                  {t(key)}
                </span>
              </a>
            ))}
          </div>
        </section>

        {/* ========== ARTICLE BODY ========== */}
        <article className="mx-auto max-w-3xl px-4 md:px-6">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-foreground-400">
            <Link to="/" className="transition-colors hover:text-primary-600">
              {t("article.breadcrumb.home")}
            </Link>
            <span className="text-foreground-300">/</span>
            <span>{t(`${prefix}.category`)}</span>
          </nav>

          {/* Lead */}
          <p className="mt-8 text-lg font-medium leading-relaxed text-foreground-800 md:text-xl">
            {t(`${prefix}.lead`)}
          </p>

          <div className="mt-10 h-px bg-background-200" />

          {/* Sections */}
          {article.sections.map((section, idx) => (
            <section key={section.h2Key} id={`section-${idx + 1}`} className="mt-12">
              <h2 className="text-2xl font-bold leading-tight text-foreground-950 md:text-3xl">
                {t(section.h2Key)}
              </h2>
              {section.paragraphKeys.map((pKey) => (
                <p key={pKey} className="mt-5 text-base leading-relaxed text-foreground-700 md:text-lg">
                  {t(pKey)}
                </p>
              ))}

              {section.stepKeys && (
                <ol className="mt-6 flex flex-col gap-3">
                  {section.stepKeys.map((stepKey, n) => (
                    <li
                      key={stepKey}
                      className="flex items-start gap-4 rounded-lg border border-background-200 bg-white p-4"
                    >
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-100 text-sm font-bold text-primary-700">
                        {n + 1}
                      </span>
                      <span className="pt-0.5 text-sm leading-relaxed text-foreground-700 md:text-base">
                        {t(stepKey)}
                      </span>
                    </li>
                  ))}
                </ol>
              )}

              {/* Inline image every 2nd section (if available) */}
              {idx < inlineImages.length && idx % 2 === 1 && (
                <div className="mt-10 overflow-hidden rounded-lg">
                  <img
                    src={inlineImages[idx]}
                    alt={t(section.h2Key)}
                    className="h-64 w-full object-cover md:h-80"
                  />
                </div>
              )}
            </section>
          ))}
        </article>

        {/* ========== GALLERY ========== */}
        <section className="mx-auto mt-16 max-w-6xl px-4 md:px-6">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-2xl font-bold text-foreground-950 md:text-3xl">
              {t("article.galleryTitle")}
            </h2>
            <p className="mt-3 text-sm text-foreground-500">
              {t("brand.tagline")}
            </p>
          </div>
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="overflow-hidden rounded-lg">
              <img
                src={galleryImage1}
                alt="Product gallery 1"
                className="h-64 w-full object-cover transition-transform duration-300 hover:scale-105 md:h-72"
              />
            </div>
            <div className="overflow-hidden rounded-lg">
              <img
                src={galleryImage2}
                alt="Product gallery 2"
                className="h-64 w-full object-cover transition-transform duration-300 hover:scale-105 md:h-72"
              />
            </div>
            <div className="overflow-hidden rounded-lg">
              <img
                src={galleryImage3}
                alt="Product gallery 3"
                className="h-64 w-full object-cover transition-transform duration-300 hover:scale-105 md:h-72"
              />
            </div>
          </div>
        </section>

        {/* ========== FAQ ========== */}
        <section className="mx-auto mt-16 max-w-3xl px-4 md:px-6">
          <h2 className="text-2xl font-bold text-foreground-950 md:text-3xl">
            {t("article.faq.h2")}
          </h2>
          <div className="mt-6">
            <Accordion items={faqItems} />
          </div>
        </section>

        {/* ========== CTA BANNER ========== */}
        <section className="mx-auto mt-20 max-w-6xl px-4 md:px-6">
          <div className="overflow-hidden rounded-2xl bg-primary-500 px-6 py-14 text-center md:py-20">
            <h3 className="text-2xl font-bold text-white md:text-4xl">
              {t(`${prefix}.cta.title`)}
            </h3>
            <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-white/90 md:text-base">
              {t(`${prefix}.cta.body`)}
            </p>
            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <a
                href={MAIN_SITE_URL}
                target="_blank"
                rel="noopener nofollow"
                className="whitespace-nowrap rounded-md bg-white px-7 py-3.5 text-sm font-semibold text-primary-700 transition-colors hover:bg-background-100 cursor-pointer"
              >
                {t(`${prefix}.cta.button`)}
              </a>
              <Link
                to="/"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-white/40 px-7 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-white/10 cursor-pointer"
              >
                <i className="ri-arrow-left-line text-sm w-4 h-4 flex items-center justify-center"></i>
                {t("article.breadcrumb.home")}
              </Link>
            </div>
          </div>
        </section>

        {/* ========== RELATED ARTICLES ========== */}
        <section className="mx-auto mt-20 max-w-6xl px-4 pb-16 md:px-6 md:pb-24">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-foreground-950 md:text-3xl">
              {t("article.relatedTitle")}
            </h2>
            <Link
              to="/"
              className="hidden items-center gap-1 text-sm font-semibold text-primary-600 transition-colors hover:text-primary-700 sm:flex"
            >
              {t("article.readNext")}
              <i className="ri-arrow-right-line text-sm w-4 h-4 flex items-center justify-center"></i>
            </Link>
          </div>

          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((a) => (
              <Link
                key={a.slug}
                to={`/guides/${a.slug}`}
                className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-white transition-transform duration-200 hover:-translate-y-1 cursor-pointer"
              >
                <div className="h-48 w-full overflow-hidden">
                  <img
                    src={a.coverImage}
                    alt={t(`${a.keyPrefix}.title`)}
                    className="h-full w-full object-cover object-center transition-transform duration-300 group-hover:scale-105"
                  />
                </div>
                <div className="flex flex-1 flex-col p-5">
                  <span className="inline-flex self-start rounded-full bg-accent-100 px-3 py-1 text-xs font-semibold text-accent-800">
                    {t(`${a.keyPrefix}.category`)}
                  </span>
                  <h3 className="mt-3 text-base font-bold leading-snug text-foreground-950 md:text-lg">
                    {t(`${a.keyPrefix}.title`)}
                  </h3>
                  <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-foreground-600">
                    {t(a.excerptKey)}
                  </p>
                  <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-primary-600">
                    {t("articles.card.readMore")}
                    <i className="ri-arrow-right-line text-sm w-4 h-4 flex items-center justify-center transition-transform group-hover:translate-x-1"></i>
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}